import {
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { Grid } from "@mui/system";
import React, { useCallback, useEffect, useState } from "react";
import dashboardService from "../../../services/DashboardServices";
import VisibilityIcon from "@mui/icons-material/Visibility";
import IdeaCapturingModalPopUp from "../../successPopUp/ideaCapturePopUp";
import { REImagingUpdateData } from "../../../types/dashboard";
import { useNavigate } from "react-router-dom";
import IdeaCapturingModal from "../../adminModals/REImagine/IdeaCaptureREModal";
import { useAppSelector } from "../../../globalState/hooks";

interface OtherDetail {
  id: number;
  value: string;
}
interface TableRowData {
  id: number;
  division: string;
  functionName: string;
  subFunctionName: string;
  otherDetailsId: (string | number)[];
  otherData?: string;
  isInitiated: boolean;
}
const REImagineSubmitList: React.FC = () => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<TableRowData[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [otherDetailsCheckBoxData, setOtherDetailsCheckBoxData] = useState<
    OtherDetail[]
  >([]);
  const [isRewardAcceptanceFormOpen, setIsRewardAcceptanceFormOpen] =
    useState(true);
  const [rewardAcceptanceFormData, setRewardAcceptanceFormData] =
    useState<REImagingUpdateData | null>(null);
  const financialYear = useAppSelector(
    (state) =>
      state.dashboardReducer?.financialYear || { startDate: "", endDate: "" }
  );
  const fetchTableData = useCallback(async () => {
    const payload = {
      pageNo: page + 1,
      pageSize: pageSize,
      startDate: financialYear.startDate,
      endDate: financialYear.endDate,
      status: null,
      isIdeathon: false,
      isInitiated: null,
      requestedBy: null,
      gender: null,
    };

    try {
      const response = await dashboardService.GetREImagineDataAPI(payload);
      setTableData(response?.data?.data?.data ?? []);
      setTotalCount(response?.data?.data?.totalRecords ?? 0);
    } catch (error) {
      console.error("Failed to fetch table data", error);
      setTableData([]);
      setTotalCount(0);
    }
  }, [page, pageSize]);
  const fetchOtherDetailsCheckBoxData = async () => {
    try {
      const response = await dashboardService.GetOtherDetailsAPI();

      if (response?.data?.data.length > 0) {
        const formattedData: OtherDetail[] = response.data.data.map(
          (item: { id: number; otherDetailsName: string }) => ({
            id: item.id,
            value: item.otherDetailsName,
          })
        );
        setOtherDetailsCheckBoxData(formattedData);
      } else {
        setOtherDetailsCheckBoxData([]);
      }
    } catch (error) {
      console.error("Error fetching other details:", error);
    }
  };
  const fetchRewardAndRecognitionStatus = async (id: number) => {
    setIsRewardAcceptanceFormOpen(true);

    try {
      const response =
        await dashboardService.GetREImagineAceecsApproveRejectAPI(id);
      const finalResponse = response.data;
      if (finalResponse.success) {
        setRewardAcceptanceFormData(finalResponse.data);
      }
    } catch (error) {
      console.error("Error fetching reward status:", error);
    }
  };
  useEffect(() => {
    fetchTableData();
    fetchOtherDetailsCheckBoxData();
  }, [page, pageSize, fetchTableData]);

  return (
    <>
      <Card
        elevation={0}
        square
        sx={{
          borderRadius: 0,
          boxShadow: "none",
          overflow: "hidden",
          maxHeight: "fit-content",
          minHeight: "fit-content",
          background: "#393939",
          color: "#ffffff",
          padding: 3,
        }}
      >
        <Typography
          fontWeight="bold"
          sx={{ mb: 2 }}
          color="#ffffff"
          className="f-16"
        >
          RE Imagine Idea List
        </Typography>

        {/* Tabs Section */}

        <CardContent sx={{ p: 0 }}>
          <TableContainer
            sx={{
              backgroundColor: "#212121",
              padding: 0,
              minHeight: "fit-content",
              borderRadius: "6px",
              overflowX: "auto",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    "Division",
                    "Function Name",
                    "SubFunction Name",
                    "Impact Areas",
                    "Status",
                    "Action",
                  ].map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        color: "#fff",
                        background: "#0A0A0A",
                        fontWeight: "600",
                        padding: "12px 16px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {tableData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      sx={{ color: "#fff", textAlign: "center" }}
                    >
                      No data available.
                    </TableCell>
                  </TableRow>
                ) : (
                  tableData.map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ color: "#fff" }}>
                        {row.division}
                      </TableCell>

                      <TableCell sx={{ color: "#fff" }}>
                        {row.functionName}
                      </TableCell>

                      <TableCell sx={{ color: "#fff", width: "20%" }}>
                        {row.subFunctionName}
                      </TableCell>

                      <TableCell sx={{ color: "#fff", width: "20%" }}>
                        {Array.isArray(row.otherDetailsId) &&
                        row.otherDetailsId.length > 0
                          ? row.otherDetailsId
                              .map((id) => {
                                if (id === "10" || id === 10) {
                                  return row.otherData; // custom value when "Others" is chosen
                                }
                                return (
                                  otherDetailsCheckBoxData.find(
                                    (q) => q.id === Number(id)
                                  )?.value || "-"
                                );
                              })
                              .join(", ")
                          : "-"}
                      </TableCell>

                      <TableCell
                        sx={{
                          color: row.isInitiated ? "#34C759" : "orange",
                          fontWeight: 600,
                        }}
                      >
                        {row.isInitiated ? "Submitted" : "Pending to Submit"}
                      </TableCell>

                      <TableCell>
                        <IconButton
                          onClick={() =>
                            fetchRewardAndRecognitionStatus(row.id)
                          }
                        >
                          <VisibilityIcon sx={{ color: "#fff" }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Grid
            sx={{
              justifyContent: "flex-end",
              alignItems: "center",
              display: "flex",
              padding: 1,
            }}
          >
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={pageSize}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{
                color: "#ffffff",
                "& .MuiSelect-icon": { color: "#ffffff" },
                "& .MuiSvgIcon-root": { color: "#ffffff" },
              }}
            />
          </Grid>
        </CardContent>
      </Card>{" "}
      {rewardAcceptanceFormData && !rewardAcceptanceFormData.isInitiated && (
        <IdeaCapturingModal
          open={isRewardAcceptanceFormOpen}
          onClose={() => {
            setIsRewardAcceptanceFormOpen(false);
            navigate("/reimagineSubmittedList");
            fetchTableData();
          }}
          onSave={() => {
            fetchTableData();
          }}
          reImagineUpdateData={rewardAcceptanceFormData}
        />
      )}
      {rewardAcceptanceFormData && rewardAcceptanceFormData.isInitiated && (
        <IdeaCapturingModalPopUp
          open={isRewardAcceptanceFormOpen}
          onClose={() => {
            setIsRewardAcceptanceFormOpen(false);
            navigate("/reimagineSubmittedList");
            fetchTableData();
          }}
          reImagineUpdateData={rewardAcceptanceFormData}
        />
      )}
    </>
  );
};

export default REImagineSubmitList;
